import {
  Badge, Button, Card, CardSection, Checkbox, EmptyState, Field, Input, Slider, Textarea, Toggle,
} from "@/components/ui";
import { formatDateTime, fromDateTimeLocal, slugify, toDateTimeLocal } from "@/lib/utils/format";
import type { Moment } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { IconClock, IconPlus, IconSparkles, IconTrash } from "../components/icons";
import { PromptHelp } from "../components/PromptHelp";

/**
 * Moments — time-boxed overrides layered on top of the published config.
 *
 * One published event can behave differently at halftime than during a
 * headliner's set without anyone republishing mid-event. Highest priority wins
 * where windows overlap; a moment scoped to specific vibes only applies there,
 * and a lower-priority global moment still covers the rest.
 */
export function MomentsTab() {
  const { draft, update } = useBuilder();
  const moments = draft.moments ?? [];
  const scenes = draft.scenes ?? [];

  function setMoments(next: Moment[]) {
    update((current) => ({ ...current, moments: next }));
  }

  function add() {
    const stamp = Date.now();
    setMoments([
      ...moments,
      {
        slug: `moment-${stamp}`,
        name: "New Moment",
        active: true,
        priority: moments.length * 10,
        starts_at: null,
        ends_at: null,
      },
    ]);
  }

  function patch(index: number, patchValue: Partial<Moment>) {
    setMoments(moments.map((item, i) => (i === index ? { ...item, ...patchValue } : item)));
  }

  const now = Date.now();

  return (
    <CardSection
      eyebrow="When"
      title="Moments"
      description="Time-boxed overrides. Use these for a halftime prompt, a sponsor takeover, or a golden-hour look — without republishing during the event."
      actions={<Button variant="outline" size="sm" icon={<IconPlus size={14} />} onClick={add}>Add Moment</Button>}
    >
      {moments.length === 0 ? (
        <EmptyState
          icon={<IconSparkles size={28} />}
          title="No moments configured"
          description="Without any moments the booth behaves the same all day, which is the right default for most events."
          action={<Button variant="primary" size="sm" onClick={add}>Add a moment</Button>}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {moments.map((moment, index) => {
            const starts = moment.starts_at ? new Date(moment.starts_at).getTime() : null;
            const ends = moment.ends_at ? new Date(moment.ends_at).getTime() : null;
            const live =
              (moment.active ?? true) &&
              (starts === null || starts <= now) &&
              (ends === null || ends > now);
            const expired = ends !== null && ends <= now;

            return (
              <Card key={`${moment.slug}-${index}`} className="flex flex-col gap-5 p-5">
                <header className="flex flex-wrap items-center gap-3">
                  <IconClock size={16} className={live ? "text-accent" : "text-haze-400"} />
                  <div className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-sm font-semibold">{moment.name || "Untitled"}</span>
                    <span className="truncate font-mono text-2xs text-haze-400">{moment.slug}</span>
                  </div>

                  <div className="ml-auto flex items-center gap-2">
                    {live && <Badge tone="accent">Active now</Badge>}
                    {expired && <Badge tone="neutral">Ended</Badge>}
                    {!live && !expired && (moment.active ?? true) && <Badge tone="aqua">Scheduled</Badge>}
                    {!(moment.active ?? true) && <Badge tone="neutral">Disabled</Badge>}
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setMoments(moments.filter((_, i) => i !== index))}
                      aria-label={`Remove ${moment.name}`}
                      className="text-haze-400 hover:text-danger"
                    >
                      <IconTrash size={14} />
                    </Button>
                  </div>
                </header>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Slug">
                    <Input className="font-mono" value={moment.slug} onChange={(e) => patch(index, { slug: slugify(e.target.value) })} />
                  </Field>
                  <Field label="Name">
                    <Input value={moment.name} onChange={(e) => patch(index, { name: e.target.value })} />
                  </Field>

                  <Field label="Starts at" hint="Leave empty to start immediately.">
                    <Input
                      type="datetime-local"
                      value={toDateTimeLocal(moment.starts_at)}
                      onChange={(e) => patch(index, { starts_at: fromDateTimeLocal(e.target.value) })}
                    />
                  </Field>
                  <Field label="Ends at" hint="Leave empty to run until the event ends.">
                    <Input
                      type="datetime-local"
                      value={toDateTimeLocal(moment.ends_at)}
                      onChange={(e) => patch(index, { ends_at: fromDateTimeLocal(e.target.value) })}
                    />
                  </Field>
                </div>

                <Field label="Description">
                  <Textarea rows={2} value={moment.description ?? ""} onChange={(e) => patch(index, { description: e.target.value })} />
                </Field>

                <Slider
                  label="Priority"
                  min={0} max={100} step={5}
                  value={moment.priority ?? 0}
                  onChange={(value) => patch(index, { priority: value })}
                  hint="Where two moments overlap in time, the higher priority wins."
                />

                <Field
                  label="Prompt override"
                  hint="Replaces the resolved scenario or vibe prompt while this moment is active. Leave empty to override only branding."
                >
                  <Textarea
                    rows={4}
                    className="font-mono text-xs"
                    value={moment.prompt_template ?? ""}
                    onChange={(e) => patch(index, { prompt_template: e.target.value || undefined })}
                  />
                </Field>

                <PromptHelp />

                <fieldset className="flex flex-col gap-3">
                  <legend className="label-eyebrow mb-2">Limit to specific vibes</legend>
                  {scenes.length === 0 ? (
                    <p className="text-xs text-haze-400">Add vibes first to scope this moment.</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {scenes.map((scene) => {
                          const selected = moment.scene_slugs?.includes(scene.slug) ?? false;
                          return (
                            <Checkbox
                              key={scene.slug}
                              label={scene.name}
                              checked={selected}
                              onChange={(checked) => {
                                const current = moment.scene_slugs ?? [];
                                patch(index, {
                                  scene_slugs: checked
                                    ? [...current, scene.slug]
                                    : current.filter((slug) => slug !== scene.slug),
                                });
                              }}
                            />
                          );
                        })}
                      </div>
                      <p className="text-xs leading-relaxed text-haze-400">
                        {moment.scene_slugs?.length
                          ? "Applies only to the vibes ticked above. Other vibes fall through to a lower-priority moment, or to their own prompt."
                          : "Applies to every vibe."}
                      </p>
                    </>
                  )}
                </fieldset>

                <Toggle
                  checked={moment.active ?? true}
                  onChange={(value) => patch(index, { active: value })}
                  label="Enabled"
                  hint="Turn off to keep the configuration without it taking effect."
                />

                {(moment.starts_at || moment.ends_at) && (
                  <p className="rounded-lg bg-ink-800 px-3 py-2 text-xs text-haze-400">
                    Window: {moment.starts_at ? formatDateTime(moment.starts_at) : "immediately"} →{" "}
                    {moment.ends_at ? formatDateTime(moment.ends_at) : "no end"}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </CardSection>
  );
}
